import { useQuery } from '@apollo/client';
import { TextField } from '@mui/material';
import {
    GridEventListener,
    GridFilterInputValueProps,
    GridFilterItem,
    GridFilterOperator,
    GridRowParams,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { chain } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import GET_COURT_CASES_PAGED from '../../../../api/apollo/query/GetCourtCasesPaged';
import { useAuth } from '../../../../api/auth';
import CourtCaseDialog from '../../../../components/CourtCaseDialog';
import { CustomDataGrid } from '../../../../components/CustomDataGrid';
import TableToolbar from '../../../../components/Table/TableToolbar';
import { DefaultTableProps } from '../../../../components/Table/types';
import { captureViewCourtCase } from '../../../../posthog';
import { CourtCaseFilters } from '../../../../types/CourtCaseFilters';
import { useSummaryTableData } from '../../SummaryTableDataContext';
import { AdvancedFilterComponent } from '../AdvancedFilter';
import columns from './columns';

const CommoditiesFilterInput = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;

    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        applyValue({ ...item, value: e.target.value });
    };

    return (
        <TextField
            InputLabelProps={{ shrink: true }}
            label="Value"
            placeholder="Filter value"
            autoFocus={true}
            variant="standard"
            onChange={handleOnChange}
        />
    );
};

const commoditiesFilters: GridFilterOperator[] = [
    {
        label: 'contains',
        value: 'contains',
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            if (!filterItem.columnField || !filterItem.value || !filterItem.operatorValue) {
                return null;
            }

            return (params): boolean => {
                if (!params.value) return false;
                return params.value.toLowerCase().includes(filterItem.value.toLowerCase());
            };
        },
        InputComponent: CommoditiesFilterInput,
        getValueAsString: (value: number) => `${value} Stars`,
    },
];

const CourtCases = ({ shrinkTitle, hideHeaders, showHeaders }: DefaultTableProps) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { tableData, setTableRows, appendTableRows, clearTableRows } = useSummaryTableData();
    const rows = tableData['court-cases'] ?? [];
    const [advancedFilterOpen, setAdvancedFilterOpen] = useState<boolean>(false);
    const [advancedFilterActive, setAdvancedFilterActive] = useState<boolean>(false);

    const [openCourtCaseId, setOpenCourtCaseId] = useState<string | null>(null);

    const [filters, setFilters] = useState<CourtCaseFilters>({});
    const filterIdRef = useRef(0);
    const [isFetchingAllPages, setIsFetchingAllPages] = useState(false);

    const handleSetFilters = (newFilters: CourtCaseFilters) => {
        clearTableRows('court-cases');
        filterIdRef.current += 1;
        setFilters(newFilters);
    };

    const PAGE_SIZE = 1000;

    const { loading, fetchMore } = useQuery(GET_COURT_CASES_PAGED, {
        variables: { filters, first: PAGE_SIZE },
        onCompleted: ({ courtCasesPaged }) => {
            // filterId prevents data spilling into tableRows between different filter query results
            const filterId = filterIdRef.current;
            const nodes = courtCasesPaged.edges.map(({ node }) => node);
            setTableRows('court-cases', nodes);

            const fetchAllPages = async (hasNextPage: boolean, endCursor: string) => {
                if (!hasNextPage || filterIdRef.current !== filterId) return;
                const { data } = await fetchMore({
                    variables: { filters, first: PAGE_SIZE, after: endCursor },
                });
                if (filterIdRef.current !== filterId) return;
                appendTableRows('court-cases', data.courtCasesPaged.edges.map(({ node }) => node));
                await fetchAllPages(
                    data.courtCasesPaged.pageInfo.hasNextPage,
                    data.courtCasesPaged.pageInfo.endCursor
                );
            };

            if (courtCasesPaged.pageInfo.hasNextPage) {
                setIsFetchingAllPages(true);
                fetchAllPages(courtCasesPaged.pageInfo.hasNextPage, courtCasesPaged.pageInfo.endCursor)
                    .finally(() => setIsFetchingAllPages(false));
            }
        },
    });

    useEffect(() => {
        if (openCourtCaseId) {
            const { eventName, payload } = captureViewCourtCase(openCourtCaseId);
            posthog?.capture(eventName, payload);
        }
    }, [openCourtCaseId, posthog]);

    const apiRef = useGridApiRef();

    const girdColumns = useMemo(
        () =>
            columns.map((col) =>
                col.field === 'commodities'
                    ? {
                          ...col,
                          filterOperators: commoditiesFilters,
                      }
                    : col
            ),
        [columns]
    );

    useEffect(() => {
        setAdvancedFilterActive(Object.keys(filters).length !== 0);
    }, [filters]);

    useEffect(() => {
        const handleEvent: GridEventListener<'rowsScroll'> = (params) => {
            params.top > 5 ? hideHeaders() : showHeaders();
        };

        apiRef.current.subscribeEvent('rowsScroll', handleEvent);
    }, [apiRef, hideHeaders, showHeaders]);

    const onAdvancedFilterClick = () => {
        setAdvancedFilterOpen(true);
    };

    const onRowClick = useCallback(
        (params: GridRowParams) => {
            const canViewDetails = user?.permissions?.map(({ codename }) => codename).includes('view_details');
            const latestApprovedCaseForm = chain(params.row.caseForms)
                .filter(({ status }) => status === 'Approved')
                .maxBy('version')
                .value();

            if (canViewDetails && latestApprovedCaseForm) {
                navigate(`/case-forms/${latestApprovedCaseForm.id}`);
                // setOpenCourtCaseId(params.row.id);
            }
        },
        [user]
    );

    return (
        <Fragment>
            <CustomDataGrid
                apiRef={apiRef}
                columns={girdColumns}
                rows={rows}
                onRowClick={onRowClick}
                checkboxSelection={false}
                loading={loading || isFetchingAllPages}
                hideFooterSelectedRowCount
                components={{ Toolbar: TableToolbar }}
                componentsProps={{
                    toolbar: {
                        title: 'Court Cases',
                        showTitle: shrinkTitle,
                        onAdvancedFilterClick,
                        advancedFilterActive,
                        csvExportEnabled:
                            !loading &&
                            !isFetchingAllPages &&
                            user?.permissions?.map(({ codename }) => codename).includes('can_export_tables'),
                        tableApi: apiRef.current,
                        excelExportEnabled: false,
                    },
                }}
            />
            <AdvancedFilterComponent
                onFilters={handleSetFilters}
                open={advancedFilterOpen}
                onOpenChange={setAdvancedFilterOpen}
            />
            <CourtCaseDialog openCourtCaseId={openCourtCaseId} setOpenCourtCaseId={setOpenCourtCaseId} />
        </Fragment>
    );
};

export default CourtCases;
